import { ExportImageType } from '../types';

/**
 * Rasterizer backing {@link captureElementToCanvas}.
 *
 * @remarks
 * Both libraries are installed so the comparison can be run against the real
 * page without a code change. Once the winner is confirmed, delete the loser's
 * branch here and drop it from `package.json` — this file is the only place
 * either one is imported, which is what makes that a one-file edit.
 */
export type ExportCaptureEngine = 'html-to-image' | 'html2canvas-pro';

/**
 * Engine used when a request does not name one.
 *
 * @remarks
 * `html-to-image` renders through an SVG `<foreignObject>`, so the browser's
 * own layout engine paints the clone — Arabic joining, bidi word order and RTL
 * box layout come out exact by construction. `html2canvas-pro` re-implements
 * CSS painting and text layout in JS, and re-implemented text layout is the
 * historical weak point for bidirectional scripts; worse, it fails by
 * *rendering wrong* rather than by throwing. In an RTL-first bilingual app
 * that asymmetry decides the default.
 */
export const EXPORT_CAPTURE_ENGINE_DEFAULT: ExportCaptureEngine = 'html-to-image';

/**
 * Capture pixel ratio used when a request does not set one. Chosen so small
 * readings stay legible when the image is opened at full size.
 */
const DEFAULT_CAPTURE_SCALE = 3;

/**
 * Breathing room added around the captured element, in CSS pixels.
 *
 * @remarks
 * A card captured at its own bounding box comes out with its border touching
 * the file edge, because the whitespace it visually sits in belongs to the
 * *page*, not to the element. The padding is re-created at capture time so the
 * exported image reads like the on-screen card rather than a crop of it.
 */
const DEFAULT_CAPTURE_PADDING = 24;

/**
 * Options accepted by the capture helpers.
 */
export interface ICaptureOptions {
  /** Pixel ratio. Defaults to `3`. */
  scale?: number;
  /**
   * Padding added around the element, in CSS pixels, filled with the capture
   * background. Defaults to {@link DEFAULT_CAPTURE_PADDING}; pass `0` to capture
   * the element flush to its own box. Ignored by the `svg` encoding, which never
   * goes through a canvas.
   */
  padding?: number;
  /** Selectors dropped from the clone before it is painted. */
  excludeSelectors?: string[];
  /** Capture background. Defaults to the element's own computed background. */
  backgroundColor?: string;
  /** Overrides {@link EXPORT_CAPTURE_ENGINE_DEFAULT}. */
  engine?: ExportCaptureEngine;
}

/**
 * Options accepted by {@link captureElementToBlob}.
 */
export interface IImageCaptureOptions extends ICaptureOptions {
  /** Encoding, and the `html-to-image` call used to produce it. Defaults to `png`. */
  imageType?: ExportImageType;
  /**
   * Encoder quality between `0` and `1`. Read only by the lossy encodings
   * (`jpeg`, `webp`); PNG and SVG ignore it.
   */
  quality?: number;
}

/**
 * Mime type each {@link ExportImageType} encodes to.
 *
 * @remarks
 * `canvas` and `Blob` name a library call rather than an encoding — both end up
 * as PNG, which is what `toBlob` produces and what a canvas encodes to by
 * default.
 */
const EXPORT_IMAGE_TYPE_MIME: Record<ExportImageType, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  canvas: 'image/png',
  Blob: 'image/png',
};

/**
 * File extension each {@link ExportImageType} is saved with.
 *
 * @remarks
 * Kept apart from the member names because two of them are call names, not
 * extensions: a file called `chart.canvas` would not open anywhere.
 */
export const EXPORT_IMAGE_TYPE_EXTENSION: Record<ExportImageType, string> = {
  png: 'png',
  jpeg: 'jpeg',
  webp: 'webp',
  svg: 'svg',
  canvas: 'png',
  Blob: 'png',
};

/**
 * Default encoding when a request does not name one.
 */
export const EXPORT_IMAGE_TYPE_DEFAULT: ExportImageType = 'png';

/**
 * Validates the element supplied for capture.
 *
 * @param element - The region to capture, as passed on the request metadata.
 * @returns The same element, once it is known to be usable.
 * @throws When nothing usable was supplied.
 *
 * @remarks
 * Taking the element itself is the point of the contract: a page holding two
 * instances of the same component still exports the right one, which a DOM id
 * cannot guarantee. A caller holding an `ElementRef` passes `ref.nativeElement`.
 */
export function resolveCaptureElement(element: HTMLElement | undefined | null): HTMLElement {

  if (element == null) {
    throw new Error('An element is required to capture an image; none was supplied on the request metadata.');
  }

  if (!(element instanceof HTMLElement)) {
    throw new Error('The element supplied for capture is not an HTMLElement.');
  }

  if (!element.isConnected) {
    throw new Error('The element supplied for capture is not attached to the document.');
  }

  return element;
}

/**
 * Paints an element onto a canvas.
 *
 * @param element - The region to capture.
 * @param options - Capture options.
 * @returns The painted canvas.
 *
 * @remarks
 * Both engines read the *computed* styles of a cloned tree, so a web font that
 * has not finished loading is measured at its fallback metrics — the cause of
 * the "first export comes out unstyled, the second is fine" class of bug. What
 * handles it is the discarded warm-up pass inside {@link captureWithHtmlToImage},
 * not a font wait: awaiting `document.fonts.ready` proved not to be enough on
 * its own, because the library's internal style cache is cold on a first call
 * whether or not the faces have loaded.
 */
export async function captureElementToCanvas(element: HTMLElement, options: ICaptureOptions = {}): Promise<HTMLCanvasElement> {

  const scale = options.scale ?? DEFAULT_CAPTURE_SCALE;
  const engine = options.engine ?? EXPORT_CAPTURE_ENGINE_DEFAULT;

  return engine === 'html2canvas-pro'
    ? captureWithHtml2Canvas(element, options, scale)
    : captureWithHtmlToImage(element, options, scale);
}

/**
 * Captures an element straight to an encoded file blob.
 *
 * @param source - The region to capture.
 * @param options - Capture and encoding options.
 * @returns The encoded blob, ready to hand to `saveAs`.
 * @throws When the element is unusable, or the encoding cannot be produced.
 *
 * @remarks
 * The entry point for the image strategy, and the one place the
 * {@link ExportImageType} → library-call mapping lives. Going to a blob
 * directly matters for `svg`, which never touches a canvas: routing it through
 * one would rasterize away the whole reason to ask for it.
 */
export async function captureElementToBlob(
  source: HTMLElement | undefined | null,
  options: IImageCaptureOptions = {},
): Promise<Blob> {

  const element = resolveCaptureElement(source);
  const imageType = options.imageType ?? EXPORT_IMAGE_TYPE_DEFAULT;
  const engine = options.engine ?? EXPORT_CAPTURE_ENGINE_DEFAULT;
  const scale = options.scale ?? DEFAULT_CAPTURE_SCALE;
  const mimeType = EXPORT_IMAGE_TYPE_MIME[imageType];

  if (engine === 'html2canvas-pro') {

    if (imageType === 'svg') {
      throw new Error(
        'SVG export is only available through the "html-to-image" engine; '
        + '"html2canvas-pro" paints onto a canvas and has no vector output to serialize.',
      );
    }

    const canvas = await captureWithHtml2Canvas(element, options, scale);

    return canvasToBlob(canvas, mimeType, options.quality);
  }

  return captureWithHtmlToImageAs(element, options, scale, imageType, mimeType);
}

/**
 * Turns a canvas into a blob.
 *
 * @param canvas - The painted canvas.
 * @param mimeType - Encoding to request. Defaults to PNG.
 * @param quality - Encoder quality between `0` and `1`, read only by the lossy
 * encodings.
 * @returns The encoded blob.
 * @throws When the browser declines to encode the canvas.
 *
 * @remarks
 * `toBlob` hands back `null` rather than throwing when encoding fails, so the
 * null is converted into a real error here instead of travelling on as an
 * empty download. Note that a *tainted* canvas is not this path: that throws
 * `SecurityError`. A `null` means the canvas was not encodable, and the
 * dimensions are what say why — `0` on either axis, or an area past the
 * browser's canvas ceiling because the pixel ratio scaled it there.
 *
 * A `null` also covers a mime type the browser declines to encode, which is
 * how an unsupported request degrades: the error names the type rather than
 * the browser quietly handing back a PNG under the asked-for extension.
 */
export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string = 'image/png',
  quality?: number,
): Promise<Blob> {

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob == null) {
          reject(new Error(
            `The captured canvas could not be encoded as ${mimeType} (${canvas.width}×${canvas.height}). `
            + 'A zero dimension means the element had no visible box; very large dimensions mean the '
            + 'capture scale pushed the canvas past the browser\'s size limit; an unusual mime type '
            + 'means this browser has no encoder for it.',
          ));

          return;
        }

        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}

/**
 * Measures the CSS box an element should be captured at.
 *
 * @param element - The region to capture.
 * @returns Whole-pixel width and height.
 * @throws When the element has no visible box to capture.
 *
 * @remarks
 * `getBoundingClientRect` is used rather than `offsetWidth` because it also
 * reports a box for elements `offsetWidth` measures as `0` — inline hosts and
 * transformed nodes among them. Dimensions are rounded *up*: rounding down a
 * fractional box crops the last row of pixels off the export.
 */
function measureCaptureBox(element: HTMLElement): { width: number; height: number } {

  const rect = element.getBoundingClientRect();
  const width = Math.ceil(rect.width);
  const height = Math.ceil(rect.height);

  if (width === 0 || height === 0) {
    throw new Error(
      `The element supplied for capture has no visible size (${width}×${height}). `
      + 'A hidden element, a collapsed container, or a host styled `display: contents` cannot be rasterized.',
    );
  }

  return { width, height };
}

/**
 * Captures through `html-to-image`.
 *
 * @param element - The region to capture.
 * @param options - Capture options.
 * @param scale - The resolved pixel ratio.
 * @returns The painted canvas.
 *
 * @remarks
 * Imported dynamically so the library lands in its own lazy chunk instead of
 * the main bundle; a user who never exports an image never downloads it.
 */
async function captureWithHtmlToImage(
  element: HTMLElement,
  options: ICaptureOptions,
  scale: number,
): Promise<HTMLCanvasElement> {

  const { toCanvas } = await import('html-to-image');
  const config = buildHtmlToImageConfig(element, options, scale);

  // The first pass populates html-to-image's internal style and web-font
  // caches; its output is routinely missing them. It is discarded, and the
  // second pass is the one that ships.
  await toCanvas(element, config);

  return padCanvas(await toCanvas(element, config), options, scale, config.backgroundColor);
}

/**
 * Re-draws a painted canvas centred inside a larger one, leaving a filled margin.
 *
 * @param canvas - The painted canvas.
 * @param options - Capture options, read for the padding.
 * @param scale - The pixel ratio the canvas was painted at.
 * @param backgroundColor - Colour the margin is filled with.
 * @returns The padded canvas, or the original when no padding was asked for.
 *
 * @remarks
 * The margin is added *after* rasterizing rather than by growing the element
 * being captured. Growing it means editing the clone's box — and both engines
 * have already pinned that box to an explicit width, so any padding or border
 * added on top either overflows the canvas and crops an edge, or eats into the
 * content and re-wraps the layout. Painting onto a bigger canvas cannot disturb
 * a layout that has already been rendered.
 *
 * The padding is multiplied by the pixel ratio because the canvas is in device
 * pixels while the option is in CSS pixels.
 */
function padCanvas(
  canvas: HTMLCanvasElement,
  options: ICaptureOptions,
  scale: number,
  backgroundColor: string,
): HTMLCanvasElement {

  const padding = Math.max(0, options.padding ?? DEFAULT_CAPTURE_PADDING);

  if (padding === 0) {
    return canvas;
  }

  const inset = Math.round(padding * scale);
  const padded = document.createElement('canvas');

  padded.width = canvas.width + inset * 2;
  padded.height = canvas.height + inset * 2;

  const context = padded.getContext('2d');

  if (context == null) {
    // No 2D context means no margin — an unpadded export beats a failed one.
    return canvas;
  }

  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, padded.width, padded.height);
  context.drawImage(canvas, inset, inset);

  return padded;
}

/**
 * Captures through `html-to-image`, in the encoding the caller asked for.
 *
 * @param element - The region to capture.
 * @param options - Capture options.
 * @param scale - The resolved pixel ratio.
 * @param imageType - The requested encoding, which names the library call.
 * @param mimeType - The mime type that encoding maps to.
 * @returns The encoded blob.
 *
 * @remarks
 * Every branch pays the same discarded warm-up pass as
 * {@link captureWithHtmlToImage}, for the same reason — the library's style and
 * font caches are per-module, not per-entry-point, so a cold `toSvg` comes out
 * as unstyled as a cold `toCanvas` does.
 *
 * `webp` has no helper of its own in the library, so it goes through the canvas
 * and the browser's own encoder. That is also the one encoding a browser may
 * refuse outright, which {@link canvasToBlob} reports by name.
 */
async function captureWithHtmlToImageAs(
  element: HTMLElement,
  options: IImageCaptureOptions,
  scale: number,
  imageType: ExportImageType,
  mimeType: string,
): Promise<Blob> {

  // `canvas` asks for the canvas call explicitly; `webp` lands here because the
  // library has no WebP helper. Both go through the canvas path, which pays for
  // its own warm-up pass, and then the browser's encoder.
  //
  // Every other raster encoding joins them once a padding margin is asked for:
  // the margin is drawn onto the canvas, so the library's own `toPng` / `toJpeg`
  // helpers — which never touch one — would come out flush to the edge. Only
  // `svg` stays on its own path, since routing a vector through a canvas would
  // rasterize away the reason to ask for it.
  if (imageType === 'canvas' || imageType === 'webp'
    || (imageType !== 'svg' && (options.padding ?? DEFAULT_CAPTURE_PADDING) > 0)) {
    return canvasToBlob(await captureWithHtmlToImage(element, options, scale), mimeType, options.quality);
  }

  const htmlToImage = await import('html-to-image');
  const config = { ...buildHtmlToImageConfig(element, options, scale), quality: options.quality };

  // Warm-up pass — discarded. See the note above.
  await htmlToImage.toCanvas(element, config);

  switch (imageType) {

    case 'Blob': {
      const blob = await htmlToImage.toBlob(element, config);

      if (blob == null) {
        throw new Error('html-to-image returned no blob for the captured element.');
      }

      return blob;
    }

    case 'jpeg':
      return dataUrlToBlob(await htmlToImage.toJpeg(element, config));

    case 'svg':
      return dataUrlToBlob(await htmlToImage.toSvg(element, config));

    case 'png':
    default:
      return dataUrlToBlob(await htmlToImage.toPng(element, config));
  }
}

/**
 * Builds the options object both `html-to-image` paths share.
 *
 * @param element - The region to capture.
 * @param options - Capture options.
 * @param scale - The resolved pixel ratio.
 * @returns The library configuration.
 */
function buildHtmlToImageConfig(element: HTMLElement, options: ICaptureOptions, scale: number) {

  const excluded = options.excludeSelectors ?? [];
  const { width, height } = measureCaptureBox(element);

  return {
    // Sized explicitly. Left to itself the library reads `offsetWidth` /
    // `offsetHeight`, which are `0` for a node with no layout box of its own —
    // a `display: contents` component host being the usual one. That produces a
    // `0 × 0` canvas, and a zero-area canvas encodes to `null` rather than
    // throwing, which is the "could not be encoded as a PNG" report.
    width,
    height,
    pixelRatio: scale,
    backgroundColor: options.backgroundColor ?? resolveBackgroundColor(element),
    cacheBust: true,
    filter: (node: HTMLElement) => !isExcluded(node, excluded),
  };
}

/**
 * Converts a data URI into a blob.
 *
 * @param dataUrl - The URI produced by one of the `html-to-image` helpers.
 * @returns The decoded blob, carrying the mime type the URI declared.
 *
 * @remarks
 * `fetch` is used rather than a manual `atob` because the helpers do not all
 * produce base64: `toSvg` returns a percent-encoded `charset=utf-8` payload,
 * which `atob` would reject. `fetch` reads both forms and keeps the declared
 * mime type on the result.
 */
async function dataUrlToBlob(dataUrl: string): Promise<Blob> {

  const response = await fetch(dataUrl);

  return response.blob();
}

/**
 * Captures through `html2canvas-pro`.
 *
 * @param element - The region to capture.
 * @param options - Capture options.
 * @param scale - The resolved pixel ratio.
 * @returns The painted canvas.
 */
async function captureWithHtml2Canvas(
  element: HTMLElement,
  options: ICaptureOptions,
  scale: number,
): Promise<HTMLCanvasElement> {

  const html2canvas = (await import('html2canvas-pro')).default;
  const excluded = options.excludeSelectors ?? [];

  // Sized explicitly for the same reason the `html-to-image` path is, and so
  // that a host with no layout box fails with a diagnosis rather than with a
  // 0 × 0 canvas that only surfaces later as an encoding error.
  const { width, height } = measureCaptureBox(element);
  const backgroundColor = options.backgroundColor ?? resolveBackgroundColor(element);

  const canvas = await html2canvas(element, {
    width,
    height,
    scale,
    backgroundColor,
    useCORS: true,
    logging: false,
    ignoreElements: (node: Element) => isExcluded(node, excluded),
  });

  return padCanvas(canvas, options, scale, backgroundColor);
}

/**
 * Tests a node against the caller's exclusion selectors.
 *
 * @param node - The node being considered for the clone.
 * @param selectors - Selectors to drop.
 * @returns Whether the node should be left out.
 *
 * @remarks
 * Both engines walk text nodes as well as elements, and a text node has no
 * `matches`, so the guard is on the method rather than on `nodeType`.
 */
function isExcluded(node: Node, selectors: string[]): boolean {

  if (selectors.length === 0) {
    return false;
  }

  const candidate = node as Element;

  if (typeof candidate.matches !== 'function') {
    return false;
  }

  return selectors.some((selector) => {
    try {
      return candidate.matches(selector);
    }
    catch {
      // A malformed selector should narrow the capture, not abort it.
      console.warn(`[export] ignoring invalid exclude selector "${selector}".`);

      return false;
    }
  });
}

/**
 * Finds the nearest opaque background behind an element.
 *
 * @param element - The region being captured.
 * @returns A CSS colour, falling back to white.
 *
 * @remarks
 * A card with a transparent background would otherwise be painted onto black
 * by both engines, so the first non-transparent ancestor colour is used —
 * which is what the element visually sits on anyway.
 */
function resolveBackgroundColor(element: HTMLElement): string {

  let current: HTMLElement | null = element;

  while (current != null) {
    const color = window.getComputedStyle(current).backgroundColor;

    if (color !== '' && color !== 'transparent' && !color.startsWith('rgba(0, 0, 0, 0)')) {
      return color;
    }

    current = current.parentElement;
  }

  return '#ffffff';
}

